import { find, findIndex } from 'lodash';

import {
  CartLineItem,
  ISpecial,
  PromoCarouselDocumentDataCarouselItemsItem,
  TreezSpecials,
} from '@/types';
import {
  collectGroupProductIds,
  getCartLinesForSpecial,
} from '@/utils/bogoSpecialCart';

import { BogoClassifyState, classifyBogoUnits } from './bogoClassify';
import { formatSpecial, getBogoFlowFlags } from './utils';

type BundleReduceState = BogoClassifyState;

export type DealBundleState = {
  bundleComplete: boolean;
  method?: string;
  specialId: string;
  title: string;
};

export type BundleSpecialProgress = {
  bundleProducts: CartLineItem[];
  currentBundleProducts: CartLineItem[];
  dependentProducts: CartLineItem[];
  independentProducts: CartLineItem[];
  initDepProducts: ISpecial['depProducts'];
  initIndProducts: ISpecial['indProducts'];
  processingDependents: boolean;
  processingIndependents: boolean;
  special?: ISpecial;
};

const classifyBundleUnits = (
  currentBundleProducts: CartLineItem[],
  products: CartLineItem[],
  formatedSpecial: ISpecial,
): BundleReduceState => {
  const independentRequired = formatedSpecial?.independentProductsRequired ?? 0;
  const dependentRequired = formatedSpecial?.dependentProductsRequired ?? 0;
  const isBogoSpecial = formatedSpecial?.method === 'BOGO';

  if (isBogoSpecial) {
    return classifyBogoUnits({
      bundleLines: currentBundleProducts,
      cartProducts: products,
      depProducts: formatedSpecial.depProducts,
      indProducts: formatedSpecial.indProducts,
      independentRequired,
      dependentRequired,
    });
  }

  const accountedProducts: CartLineItem[] = currentBundleProducts.flatMap(
    line => Array.from({ length: line.quantity }, () => ({ ...line })),
  );

  return accountedProducts.reduce<BundleReduceState>(
    (result, current) => {
      const depProducts = formatedSpecial?.depProducts;
      const indProducts = formatedSpecial?.indProducts;
      const productsInOriginalCart = products;

      const inIndependentList = !!find(indProducts, d => d?.id === current?.id);
      const inDependentList = !!find(depProducts, d => d?.id === current?.id);

      const stillNeedIndependentSlot =
        result.independentProductsInCart.length < independentRequired;
      const stillNeedDependentSlot =
        result.dependentProductsInCart.length < dependentRequired;

      let shouldBeIndependent = false;
      let shouldBeDependent = false;

      if (inIndependentList && inDependentList) {
        if (stillNeedIndependentSlot) {
          shouldBeIndependent = true;
        } else if (stillNeedDependentSlot) {
          shouldBeDependent = true;
        }
      } else if (inIndependentList) {
        shouldBeIndependent =
          !!current?.independent || stillNeedIndependentSlot;
      } else if (inDependentList) {
        shouldBeDependent = !!current?.dependent || stillNeedDependentSlot;
      } else {
        shouldBeIndependent = !!current?.independent;
        shouldBeDependent = !shouldBeIndependent && !!current?.dependent;
      }

      if (shouldBeIndependent) {
        const indexInCart = findIndex(
          productsInOriginalCart,
          p => p._id === current._id,
        );

        if (indexInCart !== -1) {
          result.independentProductsInCart.push({
            ...current,
            independent: true,
            dependent: false,
          });
        }
        return result;
      }

      if (shouldBeDependent) {
        const indexInCart = findIndex(
          productsInOriginalCart,
          p => p._id === current._id,
        );

        if (stillNeedDependentSlot && indexInCart !== -1) {
          result.dependentProductsInCart.push({
            ...current,
            dependent: true,
            independent: false,
          });
        }
        return result;
      }

      return result;
    },
    {
      activeBuyUnits: 0,
      activeGetUnits: 0,
      completedDealsInCart: 0,
      dependentProductsInCart: [],
      independentProductsInCart: [],
    },
  );
};

const computeBundleSpecialProgress = (
  cartProducts: CartLineItem[],
  currentSpecial: TreezSpecials | undefined,
  groupData: Record<string, string[]>,
  isGroupsLoading: boolean,
  cmsDiscountItem?: PromoCarouselDocumentDataCarouselItemsItem,
): BundleSpecialProgress | undefined => {
  if (!currentSpecial?.id || isGroupsLoading) {
    return undefined;
  }

  const allowDiscountProducts = !currentSpecial?.groupsRequired?.length;
  const groupProductIds = collectGroupProductIds(groupData);
  const resolvedBundleProducts = getCartLinesForSpecial(
    cartProducts,
    currentSpecial.id,
    groupProductIds,
    allowDiscountProducts,
  );

  const formatedSpecial = formatSpecial(
    resolvedBundleProducts,
    currentSpecial,
    groupData,
    isGroupsLoading,
    cmsDiscountItem,
  );

  const independentRequired = formatedSpecial?.independentProductsRequired ?? 0;
  const dependentRequired = formatedSpecial?.dependentProductsRequired ?? 0;
  const isBogoSpecial = formatedSpecial?.method === 'BOGO';

  const reduceState = classifyBundleUnits(
    resolvedBundleProducts,
    cartProducts,
    formatedSpecial,
  );

  const { dependentProductsInCart, independentProductsInCart } = reduceState;

  const i =
    formatedSpecial?.independentProductsRequired >
      independentProductsInCart?.length &&
    !formatedSpecial?.allowDiscountProducts;

  const bogoFlow = isBogoSpecial
    ? getBogoFlowFlags(
        reduceState.activeBuyUnits,
        reduceState.activeGetUnits,
        independentRequired,
        dependentRequired,
      )
    : undefined;

  const nextProcessingIndependents = isBogoSpecial
    ? bogoFlow?.processingIndependents ?? true
    : i;

  const nextProcessingDependents = isBogoSpecial
    ? bogoFlow?.processingDependents ?? false
    : !i;

  const { activeBuyUnits, activeGetUnits } = reduceState;
  const hasActiveCycle = activeBuyUnits > 0 || activeGetUnits > 0;
  const minimumCycleMet =
    dependentProductsInCart.length >= dependentRequired &&
    independentProductsInCart.length >= independentRequired;
  const bogoBundleComplete = minimumCycleMet && !hasActiveCycle;

  return {
    bundleProducts: independentProductsInCart.concat(dependentProductsInCart),
    currentBundleProducts: resolvedBundleProducts,
    dependentProducts: dependentProductsInCart,
    independentProducts: independentProductsInCart,
    initDepProducts: formatedSpecial?.depProducts,
    initIndProducts: formatedSpecial?.indProducts,
    processingDependents: nextProcessingDependents,
    processingIndependents: nextProcessingIndependents,
    special: {
      ...formatedSpecial,
      activeBundle: bogoFlow?.activeBundle,
      activeBundleHasPendingGet: bogoFlow?.activeBundleHasPendingGet,
      bundleComplete: isBogoSpecial
        ? bogoBundleComplete
        : formatedSpecial?.bundleComplete,
    },
  };
};

/** Route-scoped deal snapshot for cart badge; BOGO uses cycle-aware completion. */
const computeDealBundleState = (
  cartProducts: CartLineItem[],
  currentSpecial: TreezSpecials | undefined,
  groupData: Record<string, string[]>,
  isGroupsLoading: boolean,
  cmsDiscountItem?: PromoCarouselDocumentDataCarouselItemsItem,
): DealBundleState | undefined => {
  const progress = computeBundleSpecialProgress(
    cartProducts,
    currentSpecial,
    groupData,
    isGroupsLoading,
    cmsDiscountItem,
  );

  if (!progress?.special || !currentSpecial?.id) {
    return undefined;
  }

  return {
    bundleComplete: progress.special.bundleComplete ?? false,
    method: progress.special.method,
    specialId: progress.special.specialId ?? currentSpecial.id,
    title: progress.special.title ?? currentSpecial.title ?? '',
  };
};

export {
  classifyBundleUnits,
  computeBundleSpecialProgress,
  computeDealBundleState,
};
